import {backgroundColor, colorPaper, colorText} from "../App/theme.js";
import React, {useState, useEffect, useContext} from 'react';
import { Paper,  Box, IconButton} from '@mui/material';
import {AlertContext} from "../App/App.js";
import {isMobile, parseJSON} from "../App/utils";
import CloseIcon from '@mui/icons-material/Close';
import {CodeViewer} from "./CodeViewer";

export const createFolder = (myData, folder) => (!myData.find(o => o?.folder === folder)) && myData.push({folder, children: [], empty: true});
export const createFile = (myData, filename, parent, kbId, namespace) => {
    const i = myData.findIndex(o => o?.folder === parent);
    if (!myData[i].children.find(o => o?.filename === filename)) {
        myData[i].children.push({ filename: filename, empty: true, Key: `${namespace}/${kbId}/${parent}/${filename}` })
    }
}
export const convertS3ObjectsToTree = (s3Objects) => {
    let tree = {};

    s3Objects.forEach(obj => {
        let parts = obj.Key.split('/').slice(2); // Skip the first two parts
        let currentNode = tree;

        parts.forEach((part, index) => {
            if (index === parts.length - 1) {
                // It's a file
                if (!currentNode.children) {
                    currentNode.children = [];
                }

                currentNode.children.push({
                    filename: part,
                    Size: obj.Size,
                    Key: obj.Key,
                    LastModified: obj.LastModified || 'N/A' // Defaulting to 'N/A' if not provided
                });
            } else {
                // It's a directory
                if (!currentNode[part]) {
                    currentNode[part] = {};
                }
                currentNode = currentNode[part];
            }
        });
    });

    const convertNodeToFinalFormat = (node, name) => {
        let children = [];

        for (let key in node) {
            if (key !== 'children') {
                children.push(convertNodeToFinalFormat(node[key], key));
            } else {
                children = children.concat(node[key]);
            }
        }

        if (name) {
            return {
                folder: name,
                children: children
            };
        } else {
            return children;
        }
    };

    return convertNodeToFinalFormat(tree);
};

export const getNamespaceByKey = (key) => key.split('/')[0];
export const getFilePathByKey = (key) => key.split('/').slice(2).join('/');
export const getFilenameByKey = (key) => key.split('/').pop();

export const CodeTabIsLoading = () => (
    <div style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: colorText,
    }}>
        Loading ...
    </div>
);

export const CodeEditorConsole = ({ codeLoading, response, text, onClose, isFullscreen }) => {
    const MAX_DISPLAY_LENGTH = 500000; // Maximum characters to display
    const [imageSrc, setImageSrc] = useState('');
    const Alert = useContext(AlertContext);

    useEffect(() => {
        if(codeLoading) setImageSrc('')
    }, [codeLoading]);

    useEffect(() => {
        if (response && typeof response === 'string') {
            try {
                const parsedResponse = parseJSON(response);
                if (parsedResponse?.ContentType && parsedResponse?.base64Data) {
                    setImageSrc(`data:${parsedResponse.ContentType};base64,${parsedResponse.base64Data}`);
                }
            } catch (e) {
                console.error('Error parsing JSON:', e);
            }
        } else {
            setImageSrc('')
        }
    }, [response]);

    const shortResponse = response && response.length > MAX_DISPLAY_LENGTH
        ? `${response.substring(0, MAX_DISPLAY_LENGTH)}...`
        : response;

    const responseData = parseJSON(response)

    const finalResponse = shortResponse !== response
        ? shortResponse
        : (responseData ? JSON.stringify( responseData , null, 2 ) : response)

    return (
        <Paper sx={{
            overflow: 'auto',
            color: 'text.primary',
            padding: 0,
            marginTop: 0,
            backgroundColor: 'background.paper',
            whiteSpace: 'pre-wrap',
        }}>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    {imageSrc && (
                        <Box sx={{ mt: 2, position: 'relative' }}>
                            <IconButton
                                onClick={onClose}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8,
                                    color: 'primary.main', // Adjust color as needed
                                    zIndex: 1, // Ensure the button is above other content
                                }}
                            >
                                <CloseIcon  style={{ fontSize: '1rem' }} />
                            </IconButton>
                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                <img src={imageSrc} alt="Embedded" style={{ maxWidth: '100%', maxHeight: '100vh' }} />
                            </Box>
                        </Box>
                    )}
                <Box style={{bottom: '-18px', left:0, position:'absolute', zIndex: 100, width: '100%'}}>
                {!imageSrc && (codeLoading || response) &&
                    <CodeViewer
                        styleCopy={{
                            position: isFullscreen ? 'fixed' : 'absolute',
                            right: codeLoading ? '0px' : '40px', // Adjust position if noCopy is true
                            zIndex: 10,
                            color: 'rgba(0, 0, 0, 0.54)',
                        }}
                        styleClose={{
                            position: isFullscreen ? 'fixed' : 'absolute',
                            right: '0px',
                            zIndex: 10,
                            color: 'rgba(0, 0, 0, 0.54)',
                        }}
                        isFullscreen={isFullscreen}
                        noCopy={true}
                        onClose={!codeLoading && onClose}
                        style={{width: '100%', backgroundColor: '#181717', height: '180px'}}
                        setSystemAlert={Alert.setSystemAlert}
                        source={!!codeLoading ? text : finalResponse}
                        limitedWidth={true}
                        language={"json"}
                        className="codeWrapperAPI"
                    />
                }
                </Box>
            </Box>
        </Paper>
    );
};

export const isNewFile = (item) => {
    return item?.Size === 0 && (Date.now() - new Date(item?.LastModified).getTime()) <= 5000
}

const mobileMonacoOptions = {
    fontSize: 10,
    lineNumbers: "on",
    glyphMargin: false,
    scrollbar: {
        vertical: 'hidden',
        horizontal: 'hidden',
        verticalScrollbarSize: 0,
        horizontalScrollbarSize: 0,
        arrowSize: 0,
        handleMouseWheel: false,
    },
    minimap: {
        enabled: false,
    },
    overviewRulerLanes: 0,
    lineDecorationsWidth: 8,
    padding: {
        top: 0,
        bottom: 0,
    },
    smoothScrolling: false,
    folding: false,
    lineNumbersMinChars: 3,
    wordWrap: "off",
    renderWhitespace: "none",
    renderLineHighlight: "none",
    codeLens: false,
    bracketPairColorization: {
        enabled: false,
    },
    cursorBlinking: "solid",
}

export const MonacoOptions = isMobile ? mobileMonacoOptions : {
    // glyphMargin: true,
    fontSize: 14,
    scrollbar: {
        scrollByPage: true,
        readOnly: false,
    },
}

export const getContainerStyles = (isFullscreen) => {
    return {
        marginTop: isFullscreen ? 0 : 70,
        position: isFullscreen ? 'fixed' : 'relative',
        height: isFullscreen ? '100vh' : undefined,
        top: 0,
        left: isMobile && !isFullscreen ? -40 : 0,
        right: 0,
        marginRight: isMobile && !isFullscreen ? -80 : 0,
        bottom: 0,
        zIndex: isFullscreen ? 2000 : 1,
        backgroundColor: colorPaper,
        overflowY: 'auto'
    }
}